import React, { useEffect, useRef, useState } from 'react';
import SEO from '../../components/SEO';

const GOOGLE_CLIENT_ID = '1005874412531-nq97k0smjkvdcuf99gog38c5m54h4mvl.apps.googleusercontent.com';

const PAYPAL_PLANS = {
  basic:  { sandbox: 'P-96L44756EL916601UNC7YG4Q', production: 'P-31P388067W6088546NDBAGJY' },
  pro:    { sandbox: 'P-9GN64782NB692931SNEY7TNI', production: 'P-23K44038X52611303NGASEJI' }
};

const TIERS = {
  basic: {
    name: 'Basic', price: '14.99',
    benefits: ['750 monthly streams', '15 song uploads per month', 'Playlist analytics', 'Premium badge in-app', '14 day cash out', 'Less ads']
  },
  pro: {
    name: 'Pro', price: '24.99',
    benefits: ['Unlimited streams', 'Free uploads', 'Playlist analytics', 'Premium badge in-app', 'Priority submission to Siki playlists', 'Instant withdrawal (PayPal instant-transfer fee applies)', 'Access to PowerPlays', 'No Ads', 'Earn more']
  }
};

function getServerUrl() {
  const h = window.location.hostname;
  if (h === 'siki.io' || h === 'www.siki.io' || h === 'memehouse.siki.io') return 'https://core.siki.io';
  if (h.includes('staging')) return 'https://staging-core.siki.io';
  if (h.includes('dev')) return 'https://dev-core.siki.io';
  return 'http://localhost:4041';
}

function isProduction() {
  const h = window.location.hostname;
  return h === 'siki.io' || h === 'www.siki.io' ||
    (h.endsWith('.siki.io') && !h.includes('staging') && !h.includes('dev'));
}

const SERVER_URL = getServerUrl();

export default function SubscribePage() {
  const [step, setStep] = useState('login'); // login | tiers | result
  const [currentStep, setCurrentStep] = useState(1);
  const [authToken, setAuthToken] = useState(null);
  const [userName, setUserName] = useState('');
  const [selectedTier, setSelectedTier] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resultType, setResultType] = useState(null); // success | cancelled | subscribed
  const [resultTier, setResultTier] = useState('');
  const googleBtnRef = useRef(null);

  // Check for PayPal return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('status');
    if (status === 'success') {
      setResultType('success');
      setStep('result');
    } else if (status === 'cancelled') {
      setResultType('cancelled');
      setStep('result');
    }
  }, []);

  // Load Google Identity Services
  useEffect(() => {
    if (step !== 'login') return;

    const params = new URLSearchParams(window.location.search);
    if (params.get('status')) return;

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = () => {
      if (!googleBtnRef.current) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCallback,
        ux_mode: 'popup'
      });
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: 'filled_black',
        size: 'large',
        shape: 'pill',
        width: 280,
        text: 'signin_with'
      });
    };
    document.head.appendChild(script);
    return () => { if (script.parentNode) script.parentNode.removeChild(script); };
  }, [step]);

  const showError = (msg) => {
    setError(msg);
    setTimeout(() => setError(''), 4000);
  };

  const handleGoogleCallback = async (response) => {
    try {
      const res = await fetch(SERVER_URL + '/auth/google/web', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          idToken: response.credential,
          redirectUri: window.location.origin + '/auth/google/callback'
        })
      });

      const data = await res.json();
      if (!res.ok || !data.result || !data.result.token) {
        showError(data.message || 'Login failed. Please try again.');
        return;
      }

      const token = data.result.token;
      setAuthToken(token);
      const name = data.result.userData
        ? (data.result.userData.firstName || data.result.userData.userName || '')
        : '';
      setUserName(name);

      // Check if already subscribed
      try {
        const verifyRes = await fetch(SERVER_URL + '/paypal-subscription/verify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
          body: '{}'
        });
        const verifyData = await verifyRes.json();
        if (verifyData.isActive) {
          setResultType('subscribed');
          setResultTier(verifyData.tier === 'pro' ? 'Pro' : 'Basic');
          setStep('result');
          return;
        }
      } catch (e) { /* continue to tier selection */ }

      setCurrentStep(2);
      setStep('tiers');
    } catch (err) {
      showError('Connection error. Please check your internet and try again.');
    }
  };

  const handleSubscribe = async () => {
    if (!selectedTier || !authToken) return;
    setLoading(true);
    setCurrentStep(3);

    try {
      const env = isProduction() ? 'production' : 'sandbox';
      const planId = PAYPAL_PLANS[selectedTier][env];
      const pageUrl = window.location.origin + '/subscribe';

      const res = await fetch(SERVER_URL + '/paypal-subscription/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + authToken },
        body: JSON.stringify({
          planId,
          tier: selectedTier,
          returnUrl: pageUrl + '?status=success',
          cancelUrl: pageUrl + '?status=cancelled'
        })
      });

      const data = await res.json();
      if (data.success && data.approvalUrl) {
        window.location.href = data.approvalUrl;
      } else {
        showError(data.error || 'Failed to create subscription. Please try again.');
        setLoading(false);
        setCurrentStep(2);
      }
    } catch (err) {
      showError('Connection error. Please try again.');
      setLoading(false);
      setCurrentStep(2);
    }
  };

  return (
    <div style={styles.page}>
      <SEO
        title="Subscribe to SIKI Premium — Unlimited Streams & Uploads"
        description="Unlock unlimited streaming, free uploads, instant withdrawals, and PowerPlays access. Start earning more as an independent artist with SIKI Premium."
        path="/subscribe"
      />
      <div style={styles.container}>

        {/* Logo */}
        <div style={styles.logoSection}>
          <img src="/logo192.png" alt="Siki" style={{ height: 40, marginBottom: 12 }} onError={(e) => e.target.style.display = 'none'} />
          <h1 style={styles.title}>Siki Premium</h1>
          <p style={styles.subtitle}>Unlock unlimited streaming and more</p>
        </div>

        {/* Steps */}
        {step !== 'result' && (
          <div style={styles.steps}>
            {[['Sign In', 1], ['Choose Plan', 2], ['Subscribe', 3]].map(([label, num], i) => (
              <React.Fragment key={num}>
                {i > 0 && <div style={styles.stepDivider} />}
                <div style={{ ...styles.step, color: num < currentStep ? '#888' : num === currentStep ? '#4caf50' : '#555' }}>
                  <span style={{
                    ...styles.stepNum,
                    borderColor: num < currentStep ? '#888' : num === currentStep ? '#4caf50' : '#333',
                    background: num <= currentStep ? (num === currentStep ? '#4caf50' : '#888') : 'transparent',
                    color: num <= currentStep ? '#000' : '#555',
                  }}>{num}</span>
                  {label}
                </div>
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Login */}
        {step === 'login' && (
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Sign in to continue</h2>
            <p style={styles.cardSubtitle}>Use your Google account to get started</p>
            <div ref={googleBtnRef} style={{ display: 'flex', justifyContent: 'center' }} />
          </div>
        )}

        {/* Tier Selection */}
        {step === 'tiers' && (
          <>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <h2 style={styles.cardTitle}>Choose your plan</h2>
              <p style={styles.cardSubtitle}>{userName ? `Welcome, ${userName}!` : 'Select a plan to get started'}</p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
              {Object.entries(TIERS).map(([key, tier]) => (
                <div
                  key={key}
                  onClick={() => setSelectedTier(key)}
                  style={{
                    ...styles.tierCard,
                    borderColor: selectedTier === key ? '#4caf50' : '#222',
                    background: selectedTier === key ? '#0f1a0f' : '#141414',
                  }}
                >
                  {selectedTier === key && <span style={styles.checkmark}>&#10003;</span>}
                  <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 2 }}>{tier.name}</div>
                  <div style={{ fontSize: 24, fontWeight: 800, marginBottom: 12 }}>
                    ${tier.price} <span style={{ fontSize: 14, fontWeight: 500, color: '#888' }}>/ month</span>
                  </div>
                  <ul style={{ listStyle: 'none', padding: 0 }}>
                    {tier.benefits.map((b, i) => (
                      <li key={i} style={styles.benefit}>
                        <span style={{ color: '#4caf50', fontWeight: 700, fontSize: 12, flexShrink: 0 }}>&#10003;</span>
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <button
              onClick={handleSubscribe}
              disabled={!selectedTier || loading}
              style={{
                ...styles.subscribeBtn,
                background: (!selectedTier || loading) ? '#333' : '#4caf50',
                color: (!selectedTier || loading) ? '#666' : '#000',
                cursor: (!selectedTier || loading) ? 'not-allowed' : 'pointer',
              }}
            >
              {loading
                ? <><span style={styles.spinner} /> Redirecting to PayPal...</>
                : selectedTier
                  ? `Subscribe with PayPal — $${TIERS[selectedTier].price}/mo`
                  : 'Select a plan'}
            </button>
          </>
        )}

        {/* Result */}
        {step === 'result' && (
          <div style={styles.card}>
            {resultType === 'success' && (
              <>
                <div style={styles.resultIcon}>&#127881;</div>
                <h2 style={styles.cardTitle}>You're subscribed!</h2>
                <p style={styles.cardSubtitle}>Welcome to Siki Premium. Your subscription is now active.</p>
                <a href="/app/profile" style={styles.openAppBtn}>Open Siki</a>
              </>
            )}
            {resultType === 'subscribed' && (
              <>
                <div style={styles.resultIcon}>&#11088;</div>
                <h2 style={styles.cardTitle}>You're already subscribed!</h2>
                <p style={styles.cardSubtitle}>You have Siki Premium {resultTier}. Enjoy!</p>
                <a href="/app/profile" style={styles.openAppBtn}>Open Siki</a>
              </>
            )}
            {resultType === 'cancelled' && (
              <>
                <div style={styles.resultIcon}>&#10060;</div>
                <h2 style={styles.cardTitle}>Subscription cancelled</h2>
                <p style={styles.cardSubtitle}>No worries — you can try again anytime.</p>
                <button onClick={() => window.location.href = '/subscribe'} style={styles.retryBtn}>Try Again</button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Error Toast */}
      {error && (
        <div style={styles.errorToast}>{error}</div>
      )}

      <style>{`@keyframes sk-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

const styles = {
  page: {
    fontFamily: "var(--font-body)",
    background: '#0a0a0a',
    color: '#fff',
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    padding: '24px 16px 60px',
    WebkitFontSmoothing: 'antialiased',
  },
  container: { width: '100%', maxWidth: 440 },
  logoSection: { textAlign: 'center', marginBottom: 32 },
  title: { fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', margin: 0 },
  subtitle: { color: '#888', fontSize: 14, marginTop: 6 },
  steps: { display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 28 },
  step: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, letterSpacing: '0.02em' },
  stepNum: {
    width: 22, height: 22, borderRadius: '50%', border: '2px solid #333',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 11, fontWeight: 700,
  },
  stepDivider: { width: 20, height: 1, background: '#333', alignSelf: 'center' },
  card: {
    background: '#141414', border: '1px solid #222', borderRadius: 16,
    padding: '32px 24px', textAlign: 'center',
  },
  cardTitle: { fontSize: 18, fontWeight: 700, marginBottom: 8, marginTop: 0 },
  cardSubtitle: { color: '#888', fontSize: 13, marginBottom: 24 },
  tierCard: {
    background: '#141414', border: '2px solid #222', borderRadius: 14,
    padding: 20, cursor: 'pointer', position: 'relative',
    transition: 'border-color 0.2s, background 0.2s',
  },
  checkmark: {
    position: 'absolute', top: 14, right: 16,
    background: '#4caf50', color: '#000',
    width: 22, height: 22, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13, fontWeight: 700,
  },
  benefit: {
    fontSize: 13, color: '#bbb', padding: '3px 0',
    display: 'flex', alignItems: 'center', gap: 8,
  },
  subscribeBtn: {
    width: '100%', padding: 16, border: 'none', borderRadius: 12,
    fontSize: 16, fontWeight: 700, letterSpacing: '-0.01em',
    transition: 'opacity 0.2s',
  },
  spinner: {
    display: 'inline-block', width: 18, height: 18,
    border: '2px solid #000', borderTopColor: 'transparent',
    borderRadius: '50%', animation: 'sk-spin 0.6s linear infinite',
    verticalAlign: 'middle', marginRight: 8,
  },
  resultIcon: { fontSize: 48, marginBottom: 16 },
  openAppBtn: {
    display: 'inline-block', marginTop: 20, padding: '14px 32px',
    background: '#4caf50', color: '#000', fontWeight: 700, fontSize: 15,
    borderRadius: 12, textDecoration: 'none',
  },
  retryBtn: {
    display: 'inline-block', marginTop: 16, padding: '12px 28px',
    background: '#222', color: '#fff', fontWeight: 600, fontSize: 14,
    border: '1px solid #333', borderRadius: 10, cursor: 'pointer',
  },
  errorToast: {
    position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)',
    background: '#d32f2f', color: '#fff', padding: '12px 20px',
    borderRadius: 10, fontSize: 13, fontWeight: 600, zIndex: 100,
    maxWidth: '90%', textAlign: 'center',
  },
};
